import type { FC } from "react";

import {
  useGetCurrentAccountIfLiveElseTradingAccount,
  useGetIsLegalEntity,
  useGetMargin,
  useGetOwnerDetails,
  useGetOwners,
  useIsCryptoSupported,
} from "~/v2/api/hooks";
import { useGetFPSLStatus } from "~/v2/api/hooks/useGetFPSL";
import { Page } from "~/v2/components";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { useIsEligibleForFPSL } from "../fpslOnboarding/util";
import { getIsEligibleForHighYieldCash } from "../highYieldCash/util";
import { getCanTransferCrypto } from "../profile";
import {
  CryptoCard,
  EliteCard,
  FPSLCard,
  MarketDataCard,
  OptionsCard,
} from "./cards";
import { HighYieldCard } from "./cards/HighYieldCard";

const PlansAndFeatures: FC = () => {
  const { product } = useAccountContext();

  const { account: liveAccount } =
    useGetCurrentAccountIfLiveElseTradingAccount();

  const { details } = useGetOwnerDetails();

  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();
  const { owners } = useGetOwners({
    accountId: liveAccount?.id ?? "",
    options: { enabled: !!liveAccount?.id },
  });
  const owner = owners?.find((owner) => owner.primary === true);

  const { margin } = useGetMargin({
    accountID: liveAccount?.id,
    paper: product === "paper",
    options: {
      enabled: !!liveAccount?.id && product === "live",
    },
  });

  const { status: fpslStatus } = useGetFPSLStatus({
    accountID: liveAccount?.id,
    product,
  });

  const { isCryptoSupported } = useIsCryptoSupported();

  const isEliteDisplayed = product === "live";
  const isOptionsDisplayed = product === "live";
  const isCryptoDisplayed =
    product === "live" && getCanTransferCrypto(liveAccount, isCryptoSupported);

  const isHighYieldDisplayed = getIsEligibleForHighYieldCash({
    product,
    currency: liveAccount?.currency,
    accountStatus: liveAccount?.status,
  });

  const isEligibleForFPSL = useIsEligibleForFPSL({
    product,
    margin,
    owner,
    ownerDetails: details,
    isLegalEntity,
    isLegalEntityLoading,
  });

  const isFPSLDisplayed = fpslStatus?.status === "ACTIVE" || isEligibleForFPSL;

  return (
    <Page
      title="Plans & Features"
      className="p-8 pt-0 xs:pl-0 md:p-8 md:pt-0 md:pl-0"
    >
      {isEliteDisplayed && <EliteCard account={liveAccount} />}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {<MarketDataCard account={liveAccount} />}
        {isOptionsDisplayed && <OptionsCard />}
        {isCryptoDisplayed && <CryptoCard account={liveAccount} />}
        {isFPSLDisplayed && (
          <FPSLCard account={liveAccount} product={product} />
        )}
        {isHighYieldDisplayed && <HighYieldCard account={liveAccount} />}
      </div>
    </Page>
  );
};

export default PlansAndFeatures;
